import React, { useState, useEffect, useRef } from 'react';
import { Save, Shield, AlertCircle, Building2, CalendarClock, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Label } from '@/app/components/ui/label';
import { Switch } from '@/app/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import { useComplianceProfile, useUpdateComplianceProfile, useResetComplianceDefaults } from '@/hooks/use-compliance-profile';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

const COUNTRY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SG', label: 'Singapore' },
  { value: 'NO', label: 'Norway' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'IE', label: 'Ireland' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'OM', label: 'Oman' },
];

export function ComplianceProfilePage() {
  const timeZone = useOrgTimeZone();
  const { canManageOrganization } = usePermissions();
  const { data: profile, isLoading, error } = useComplianceProfile();
  const updateProfile = useUpdateComplianceProfile();
  const resetDefaults = useResetComplianceDefaults();

  const [isOperator, setIsOperator] = useState(true);
  const [isTrainer, setIsTrainer] = useState(false);
  const [operatesVenues, setOperatesVenues] = useState(false);
  const [usesSubcontractors, setUsesSubcontractors] = useState(false);
  const [issuesWorkPacks, setIssuesWorkPacks] = useState(true);
  const [managesEquipment, setManagesEquipment] = useState(true);
  const [country, setCountry] = useState('__none');
  const [nextAssessmentDate, setNextAssessmentDate] = useState('');
  const [documentReviewToleranceDays, setDocumentReviewToleranceDays] = useState(0);
  const [toleranceError, setToleranceError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const dirtyRef = useRef(false);

  useEffect(() => {
    if (profile && !dirtyRef.current) {
      setIsOperator(profile.is_operator);
      setIsTrainer(profile.is_trainer);
      setOperatesVenues(profile.operates_venues);
      setUsesSubcontractors(profile.uses_subcontractors);
      setIssuesWorkPacks(profile.issues_work_packs_via_h3project);
      setManagesEquipment(profile.manages_equipment_in_h3gear);
      setCountry(profile.country || '__none');
      setNextAssessmentDate(profile.next_assessment_date || '');
      setDocumentReviewToleranceDays(profile.document_review_tolerance_days || 0);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      const dirty =
        isOperator !== profile.is_operator ||
        isTrainer !== profile.is_trainer ||
        operatesVenues !== profile.operates_venues ||
        usesSubcontractors !== profile.uses_subcontractors ||
        issuesWorkPacks !== profile.issues_work_packs_via_h3project ||
        managesEquipment !== profile.manages_equipment_in_h3gear ||
        country !== (profile.country || '__none') ||
        nextAssessmentDate !== (profile.next_assessment_date || '') ||
        documentReviewToleranceDays !== (profile.document_review_tolerance_days || 0);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(dirty);
      dirtyRef.current = dirty;
    }
  }, [isOperator, isTrainer, operatesVenues, usesSubcontractors, issuesWorkPacks, managesEquipment, country, nextAssessmentDate, documentReviewToleranceDays, profile]);

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  Only administrators can modify the compliance profile.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleToleranceChange = (value: string) => {
    setToleranceError(null);
    const num = Number(value);
    if (Number.isNaN(num)) {
      setDocumentReviewToleranceDays(0);
      return;
    }
    setDocumentReviewToleranceDays(Math.max(0, Math.round(num)));
  };

  const handleSave = async () => {
    try {
      setToleranceError(null);
      await updateProfile.mutateAsync({
        is_operator: isOperator,
        is_trainer: isTrainer,
        operates_venues: operatesVenues,
        uses_subcontractors: usesSubcontractors,
        issues_work_packs_via_h3project: issuesWorkPacks,
        manages_equipment_in_h3gear: managesEquipment,
        country: country === '__none' ? '' : country,
        next_assessment_date: nextAssessmentDate || null,
        document_review_tolerance_days: documentReviewToleranceDays,
      });
      toast.success('Profile Saved', {
        description: 'Compliance profile has been updated. Clause applicability will reflect the new settings.',
      });
      dirtyRef.current = false;
    } catch (err) {
      const axiosError = err as { response?: { data?: { document_review_tolerance_days?: string | string[] } } };
      const fieldError = axiosError.response?.data?.document_review_tolerance_days;
      if (fieldError) {
        setToleranceError(Array.isArray(fieldError) ? fieldError.join(' ') : String(fieldError));
      } else {
        toast.error('Error', {
          description: 'Failed to save compliance profile. Please try again.',
        });
      }
    }
  };

  const handleReset = () => {
    if (profile) {
      dirtyRef.current = false;
      setToleranceError(null);
      setIsOperator(profile.is_operator);
      setIsTrainer(profile.is_trainer);
      setOperatesVenues(profile.operates_venues);
      setUsesSubcontractors(profile.uses_subcontractors);
      setIssuesWorkPacks(profile.issues_work_packs_via_h3project);
      setManagesEquipment(profile.manages_equipment_in_h3gear);
      setCountry(profile.country || '__none');
      setNextAssessmentDate(profile.next_assessment_date || '');
      setDocumentReviewToleranceDays(profile.document_review_tolerance_days || 0);
    }
  };

  const handleResetDefaults = async () => {
    try {
      const result = await resetDefaults.mutateAsync();
      dirtyRef.current = false;
      setToleranceError(null);
      const p = result.profile;
      setIsOperator(p.is_operator);
      setIsTrainer(p.is_trainer);
      setOperatesVenues(p.operates_venues);
      setUsesSubcontractors(p.uses_subcontractors);
      setIssuesWorkPacks(p.issues_work_packs_via_h3project);
      setManagesEquipment(p.manages_equipment_in_h3gear);
      setCountry(p.country || '__none');
      setNextAssessmentDate(p.next_assessment_date || '');
      setDocumentReviewToleranceDays(p.document_review_tolerance_days || 0);
      toast.success('Defaults Restored', {
        description: `Scope flags reset to ${result.industry} defaults. Clause applicability updated.`,
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to reset scope flags to defaults. Please try again.',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Profile</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load compliance profile. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Compliance Profile</h1>
          <p className="text-gray-600 mt-1">
            Configure which compliance clauses apply to your organisation
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleResetDefaults}
            disabled={resetDefaults.isPending}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {resetDefaults.isPending ? 'Resetting...' : 'Reset to org-type defaults'}
          </Button>
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Discard
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || updateProfile.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organisation Type
          </CardTitle>
          <CardDescription>
            These flags determine which QP-300 scope tags (O/T/V) apply to your organisation.
            Only clauses matching your profile will appear in assessments and clause lists.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="is-operator">Rope Access Operator</Label>
              <p className="text-sm text-gray-500">
                Organisation operates rope access work sites
              </p>
            </div>
            <Switch
              id="is-operator"
              checked={isOperator}
              onCheckedChange={setIsOperator}
            />
          </div>

          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="is-trainer">Training Company</Label>
              <p className="text-sm text-gray-500">
                IRATA / SPRAT training company
              </p>
            </div>
            <Switch
              id="is-trainer"
              checked={isTrainer}
              onCheckedChange={setIsTrainer}
            />
          </div>

          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="operates-venues">Operates Training Venues</Label>
              <p className="text-sm text-gray-500">
                Organisation operates training venues (QP-300 Section 6)
              </p>
            </div>
            <Switch
              id="operates-venues"
              checked={operatesVenues}
              onCheckedChange={setOperatesVenues}
            />
          </div>

          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="uses-subcontractors">Uses Subcontractors</Label>
              <p className="text-sm text-gray-500">
                Organisation uses subcontracted rope access workers
              </p>
            </div>
            <Switch
              id="uses-subcontractors"
              checked={usesSubcontractors}
              onCheckedChange={setUsesSubcontractors}
            />
          </div>

          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="issues-work-packs">Issues Work Packs via H3Books</Label>
              <p className="text-sm text-gray-500">
                Organisation issues work packs via the H3Books module
              </p>
            </div>
            <Switch
              id="issues-work-packs"
              checked={issuesWorkPacks}
              onCheckedChange={setIssuesWorkPacks}
            />
          </div>

          <div className="flex items-center justify-between max-w-lg">
            <div className="space-y-0.5">
              <Label htmlFor="manages-equipment">Manages Equipment in H3Gear</Label>
              <p className="text-sm text-gray-500">
                Organisation manages equipment via the H3Gear module
              </p>
            </div>
            <Switch
              id="manages-equipment"
              checked={managesEquipment}
              onCheckedChange={setManagesEquipment}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Country
          </CardTitle>
          <CardDescription>
            Primary country of operation (ISO 3166-1 alpha-2)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="max-w-xs">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value || '__none'} value={opt.value || '__none'}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Next IRATA Assessment Date
          </CardTitle>
          <CardDescription>
            Set your next scheduled IRATA assessment date to power the Audit Readiness countdown on the board.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            type="date"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={nextAssessmentDate}
            onChange={(e) => setNextAssessmentDate(e.target.value)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Document Review Tolerance
          </CardTitle>
          <CardDescription>
            Number of days a controlled document may be past its review date before
            clause 2.8 treats it as overdue. Inside this window the clause reads
            amber; beyond it, red. Applies to clause 2.8 document control only —
            every other document check flags an overdue review straight away. Set
            to 0 to remove the amber band. Default 7 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            type="number"
            min={0}
            className={`border rounded-md px-3 py-2 text-sm max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              toleranceError ? 'border-red-500' : 'border-gray-300'
            }`}
            value={documentReviewToleranceDays}
            onChange={(e) => handleToleranceChange(e.target.value)}
          />
          {toleranceError && (
            <p className="text-red-600 text-sm mt-1">{toleranceError}</p>
          )}
        </CardContent>
      </Card>

      {profile && (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 space-y-1">
              <p>Active scope tags: {profile.active_scope_tags.length > 0 ? profile.active_scope_tags.join(', ') : 'None'}</p>
              <p>Last updated: {formatDateTimeInTimeZone(profile.updated_at, timeZone)}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
